import { useState, useEffect } from "react";
import styles from "./AdminPanel.module.css";

export default function AdminPanel() {
  const [auth, setAuth] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [codeTenantName, setCodeTenantName] = useState("");
  const [codeCount, setCodeCount] = useState(1);
  const [codes, setCodes] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleUnlock = () => {
    if (auth === "supersecretadminpass") {
      setUnlocked(true);
      setMessage("Zalogowano pomyślnie");
      setMessageType("success");
    } else {
      setMessage("Nieprawidłowe hasło");
      setMessageType("error");
    }
  };

  const showMessage = (msg, type = "success") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(""), 5000);
  };

  const createTenant = async () => {
    if (!tenantName.trim()) {
      showMessage("Wprowadź nazwę tenanta", "error");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/admin/create-tenant", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          name: tenantName,
          secret: auth
        })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Błąd tworzenia tenanta");
      }

      const data = await res.json();
      showMessage(`Tenant utworzony: ${data.tenant.name}`);
      setTenantName("");
    } catch (err) {
      showMessage(err.message || "Błąd", "error");
    } finally {
      setLoading(false);
    }
  };

  const generateCodes = async () => {
    if (!codeTenantName.trim()) {
      showMessage("Wprowadź nazwę tenanta", "error");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/admin/generate-invites", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          tenantName: codeTenantName,
          count: codeCount,
          secret: auth
        })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Błąd generowania kodów");
      }

      const data = await res.json();
      setCodes(data.codes);
      showMessage(`Wygenerowano ${data.codes.length} kodów`);
    } catch (err) {
      showMessage(err.message || "Błąd", "error");
    } finally {
      setLoading(false);
    }
  };

  // User management functions (to be implemented on backend)
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/users", {
        headers: { "Authorization": `Bearer ${auth}` }
      });
      if (res.ok) {
        const data = await res.json();
        console.log("Users response:", data); // Debug
        setUsers(data || []);
      }
    } catch (err) {
      showMessage("Błąd pobierania użytkowników", "error");
    } finally {
      setLoading(false);
    }
  };

  // Tenant management functions
  const fetchTenants = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/tenants", {
        headers: { "Authorization": `Bearer ${auth}` }
      });
      if (res.ok) {
        const data = await res.json();
        console.log("Tenants response:", data); // Debug
        setTenants(data || []);
      }
    } catch (err) {
      showMessage("Błąd pobierania tenantów", "error");
    } finally {
      setLoading(false);
    }
  };

  // Invite codes management functions
  const fetchInviteCodes = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/generate-invites", {
        headers: { "Authorization": `Bearer ${auth}` }
      });
      if (res.ok) {
        const data = await res.json();
        console.log("Invite codes response:", data); // Debug
        setInviteCodes(data || []);
      }
    } catch (err) {
      showMessage("Błąd pobierania kodów zaproszeniowych", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteInviteCode = async (inviteId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć ten kod zaproszeniowy?")) return;
    
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/generate-invites/${inviteId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${auth}` }
      });
      
      if (res.ok) {
        showMessage("Kod zaproszeniowy usunięty");
        fetchInviteCodes(); // Odśwież listę
      } else {
        throw new Error("Błąd usuwania kodu");
      }
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć tego użytkownika?")) return;
    
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${auth}` }
      });
      
      if (res.ok) {
        showMessage("Użytkownik usunięty");
        fetchUsers();
      } else {
        throw new Error("Błąd usuwania");
      }
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    if (unlocked) {
      fetchUsers();
      fetchTenants();
      fetchInviteCodes();
    }
  }, [unlocked, auth]);

  if (!unlocked) {
    return (
      <div className={styles["admin-login"]}>
        <div className={styles["login-container"]}>
          <div className={styles["login-header"]}>
            <h1>🔐 Panel Administratora</h1>
            <p>Zaloguj się, aby uzyskać dostęp</p>
          </div>
          
          <div className={styles["login-form"]}>
            <div className={styles["input-group"]}>
              <label htmlFor="admin-password">Hasło administratora</label>
              <input
                id="admin-password"
                type="password"
                placeholder="Wprowadź hasło"
                value={auth}
                onChange={(e) => setAuth(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleUnlock()}
              />
            </div>
            
            <button 
              className={styles["login-btn"]}
              onClick={handleUnlock}
              disabled={!auth.trim()}
            >
              Zaloguj się
            </button>
          </div>

          {message && (
            <div className={`${styles.message} ${styles[messageType]}`}>
              {message}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles["admin-panel"]}>
      <header className={styles["panel-header"]}>
        <h1>⚙️ Panel Administratora</h1>
        <button 
          className={styles["logout-btn"]}
          onClick={() => setUnlocked(false)}
        >
          Wyloguj się
        </button>
      </header>

      {message && (
        <div className={`${styles.message} ${styles[messageType]}`}>
          {message}
        </div>
      )}

      <div className={styles["panel-content"]}>
                {/* Tenant Management */}
        <section className={styles["admin-section"]}>
          <h2>🏢 Zarządzanie Tenantami</h2>
          
          <div className={styles["form-card"]}>
            <h3>➕ Dodaj nowego tenanta</h3>
            <div className={styles["form-row"]}>
              <div className={styles["input-group"]}>
                <label htmlFor="tenant-name">Nazwa tenanta</label>
                <input
                  id="tenant-name"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  placeholder="np. Firma ABC"
                  disabled={loading}
                />
              </div>
              <button 
                className={styles["btn-primary"]}
                onClick={createTenant}
                disabled={loading || !tenantName.trim()}
              >
                {loading ? "Tworzenie..." : "Dodaj tenanta"}
              </button>
            </div>
          </div>

          {/* Tenants List */}
          <div className={styles["form-card"]}>
            <h3>📋 Lista Tenantów</h3>
            
            <div className={styles["users-header"]}>
              <button 
                className={styles["btn-secondary"]}
                onClick={fetchTenants}
                disabled={loading}
              >
                🔄 Odśwież listę
              </button>
            </div>

            {loading ? (
              <div className={styles.loading}>Ładowanie...</div>
            ) : tenants.length > 0 ? (
              <div className={styles["users-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>ID Tenanta</th>
                      <th>Nazwa Tenanta</th>
                      <th>Liczba Użytkowników</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((tenant) => (
                      <tr key={tenant.id}>
                        <td>{tenant.id}</td>
                        <td>{tenant.name}</td>
                        <td>{users.filter(user => user.tenantId === tenant.id).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={styles["no-users"]}>
                <p>Brak tenantów do wyświetlenia</p>
              </div>
            )}
          </div>
        </section>

        {/* Generate Invite Codes */}
        <section className={styles["admin-section"]}>
          <h2>🎟️ Generowanie Kodów Zaproszeniowych</h2>
          
          <div className={styles["form-card"]}>
            <h3>🎫 Generuj kody zaproszeniowe</h3>
            <div className={styles["form-row"]}>
              <div className={styles["input-group"]}>
                <label htmlFor="code-tenant">Nazwa tenanta</label>
                <input
                  id="code-tenant"
                  value={codeTenantName}
                  onChange={(e) => setCodeTenantName(e.target.value)}
                  placeholder="np. Firma ABC"
                  disabled={loading}
                />
              </div>
              <div className={styles["input-group"]}>
                <label htmlFor="code-count">Liczba kodów</label>
                <input
                  id="code-count"
                  type="number"
                  min={1}
                  max={100}
                  value={codeCount}
                  onChange={(e) => setCodeCount(parseInt(e.target.value) || 1)}
                  disabled={loading}
                />
              </div>
              <button 
                className={styles["btn-primary"]}
                onClick={generateCodes}
                disabled={loading || !codeTenantName.trim()}
              >
                {loading ? "Generowanie..." : "Generuj kody"}
              </button>
            </div>

            {codes.length > 0 && (
              <div className={styles["codes-display"]}>
                <h4>📋 Wygenerowane kody:</h4>
                <div className={styles["codes-list"]}>
                  {codes.map((code, index) => (
                    <div key={index} className={styles["code-item"]}>
                      <code>{code}</code>
                      <button 
                        className={styles["copy-btn"]}
                        onClick={() => navigator.clipboard.writeText(code)}
                        title="Kopiuj kod"
                      >
                        📋
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Invite Codes List */}
          <div className={styles["form-card"]}>
            <h3>📋 Lista Kodów Zaproszeniowych</h3>
            
            <div className={styles["users-header"]}>
              <button 
                className={styles["btn-secondary"]}
                onClick={fetchInviteCodes}
                disabled={loading}
              >
                🔄 Odśwież listę
              </button>
            </div>

            {loading ? (
              <div className={styles.loading}>Ładowanie...</div>
            ) : inviteCodes.length > 0 ? (
              <div className={styles["users-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>Kod</th>
                      <th>Status</th>
                      <th>ID Tenanta</th>
                      <th>Nazwa Tenanta</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inviteCodes.map((code) => (
                      <tr key={code.kod}>
                        <td>
                          <code className={styles["code-display"]}>{code.kod}</code>
                        </td>
                        <td>
                          <span className={`${styles.status} ${styles[code.used ? 'used' : 'unused']}`}>
                            {code.used ? '🟢 Użyty' : '🟡 Nieużyty'}
                          </span>
                        </td>
                        <td>{code.tenantId}</td>
                        <td>{code.tenantName}</td>
                        <td className={styles.actions}>
                          <button 
                            className={styles["btn-delete"]}
                            onClick={() => deleteInviteCode(code.kod)}
                            title="Usuń kod"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={styles["no-users"]}>
                <p>Brak kodów zaproszeniowych do wyświetlenia</p>
              </div>
            )}
          </div>
        </section>



        {/* User Management */}
        <section className={styles["admin-section"]}>
          <h2>👥 Zarządzanie Użytkownikami</h2>
          
          <div className={styles["users-header"]}>
            <button 
              className={styles["btn-secondary"]}
              onClick={fetchUsers}
              disabled={loading}
            >
              🔄 Odśwież listę
            </button>
          </div>

          {loading ? (
            <div className={styles.loading}>Ładowanie...</div>
          ) : users.length > 0 ? (
            <div className={styles["users-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID Użytkownika</th>
                    <th>ID Tenanta</th>
                    <th>Nazwa Tenanta</th>
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.tenantId}</td>
                      <td>{user.tenantName || "Brak nazwy"}</td>
                      <td className={styles.actions}>
                        
                        <button 
                          className={styles["btn-delete"]}
                          onClick={() => deleteUser(user.id)}
                          title="Usuń"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles["no-users"]}>
              <p>Brak użytkowników do wyświetlenia</p>
            </div>
          )}


        </section>
      </div>
    </div>
  );
}
